import React, { useEffect, useState } from "react";
import {
    Card, Typography, Form, Input, Select, DatePicker, TimePicker, Button,
    Row, Col, Space, Upload, message, Tag, Checkbox, Switch,
} from "antd";
import { ArrowLeftOutlined, InboxOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MeetingService } from "../../../Services/Manage/MeetingService";
import { RequirementService } from "../../../Services/Manage/RequirementService";
import { MilestoneService } from "../../../Services/Manage/MilestoneService";
import { ProjectTaskService } from "../../../Services/Manage/ProjectTaskService";
import { UserService } from "../../../Services/Manage/UserService";
import { ENDPOINT } from "../../../Services/ENDPOINT";
import { ProjectService } from "../../../Services/Manage/ProjectService";
import { EmailConfigService } from "../../../Services/Manage/EmailConfigService";


const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

const CardTitle = ({ no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const priorityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const statusOptions = [
    { value: "Scheduled", label: "Scheduled" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Postponed", label: "Postponed" },
];

const timeZoneOptions = [
    { value: "(GMT+05:30) Asia/Kolkata", label: "(GMT+05:30) Asia/Kolkata" },
    { value: "(GMT+04:00) Asia/Dubai", label: "(GMT+04:00) Asia/Dubai" },
    { value: "(GMT+00:00) UTC", label: "(GMT+00:00) UTC" },
];

const AddMeeting = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const [optionalAttendeeSearch, setOptionalAttendeeSearch] = useState("");
    const [optionalAttendeeEmailSearch, setOptionalAttendeeEmailSearch] = useState("");

    const [meetingTypeOptions, setMeetingTypeOptions] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [requirementOptions, setRequirementOptions] = useState([]);
    const [milestoneOptions, setMilestoneOptions] = useState([]);
    const [taskOptions, setTaskOptions] = useState([]);
    const [fileList, setFileList] = useState([]);

    const [projectOptions, setProjectOptions] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState(location.state?.id || null);

    const projectId = selectedProjectId;
    const selectedProject = projectOptions.find((p) => p.value === selectedProjectId);
    const projectName = selectedProject?.label || location.state?.pro_name || "-";
    const projectNo = selectedProject?.pro_no || location.state?.pro_no || "-";

    const meetingDbId = location.state?.meetingDbId || null;
    const enableEdit = location.state?.enableEdit ?? true;
    const isEditMode = !!meetingDbId;
    const readOnly = isEditMode && !enableEdit;
    const [optionalAttendeeOptions, setOptionalAttendeeOptions] = useState([]);
    const [meetingCode, setMeetingCode] = useState("MTG-001");
    const [loadingRecord, setLoadingRecord] = useState(false);
    
    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [organizerOptions, setOrganizerOptions] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const summary = Form.useWatch([], form) || {};

    useEffect(() => {
        (async () => {
            try {
                const user = await UserService.getOmeUser();
                setCurrentUser(user);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    const getPermission = (link) => {
        try {
            const raw = localStorage.getItem("ome_permission");
            const parsed = JSON.parse(raw);
            const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
            return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
        } catch {
            return { add: false, edit: false, delete: false, read: false, view_all: false };
        }
    };
    const perm = getPermission("/overview-project/meetings");

    useEffect(() => {
        if (isEditMode) {
            getMeetingById(meetingDbId);
        } else {
            getLastMeeting();
        }
    }, [meetingDbId]);

    useEffect(() => {
        getDropdownValues("meeting_type", setMeetingTypeOptions);
    }, []);

    useEffect(() => {
        if (!perm.view_all && !currentUser) return;
        getProjectOptions();
    }, [currentUser]);

    useEffect(() => {
        if (!projectId) {
            setOptionalAttendeeOptions([]);
            return;
        }
        getDropdownValues("optional_attendees", setOptionalAttendeeOptions, projectId);
    }, [projectId]);


    useEffect(() => {
        getAssigneeOptions();
        getOrganizerOptions();
        getRequirementOptions();
        getMilestoneOptions();
        getTaskOptions();
    }, [selectedProjectId, projectOptions]);

    const getDropdownValues = async (field, setter, pro_id) => {
        try {
            const request = await MeetingService.getDropdownValues(field, pro_id);
            if (request?.status) {
                setter((request.data || []).map((v) => ({ value: v, label: v })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getRequirementOptions = async () => {
        if (!projectId) {
            setRequirementOptions([]);
            return;
        }
        try {
            const request = await RequirementService.getAllByProject(projectId);
            if (request?.status) {
                setRequirementOptions(
                    (request.data || []).map((r) => ({
                        value: r.req_id,
                        label: `${r.req_id} - ${r.requirement_title}`,
                    }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getMilestoneOptions = async () => {
        if (!projectId) {
            setMilestoneOptions([]);
            return;
        }
        try {
            const request = await MilestoneService.getAllByProject(projectId);
            if (request?.status) {
                setMilestoneOptions(
                    (request.data || []).map((m) => ({ value: m.milestone_code, label: m.milestone_name }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };
    const getTaskOptions = async () => {
        if (!projectId) {
            setTaskOptions([]);
            return;
        }
        try {
            const request = await ProjectTaskService.getAllByProject(projectId);
            if (request?.status) {
                setTaskOptions(
                    (request.data || []).map((t) => ({ value: t.task_code, label: `${t.task_code} - ${t.task_name}` }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getAssigneeOptions = async () => {
        if (!projectId) {
            setAssigneeOptions([]);
            return;
        }
        try {
            let engineerIds = selectedProject?.pro_engineer;
            if (typeof engineerIds === "string") {
                try { engineerIds = JSON.parse(engineerIds); } catch { engineerIds = []; }
            }
            const allowedIds = new Set((engineerIds || []).filter(Boolean).map((id) => String(id).trim()));

            const request = await UserService.getAllUsers();
            const users = request?.data || [];
            setAllUsers(users);
            const relevant = allowedIds.size
                ? users.filter((u) => allowedIds.has(String(u.user_id).trim()))
                : users;

            setAssigneeOptions(
                relevant.map((u) => ({
                    value: u.name,
                    label: `${u.name}${u.user_id ? ` (${u.user_id})` : ""}`,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };


    const getOrganizerOptions = async () => {
        if (!projectId) {
            setOrganizerOptions([]);
            return;
        }
        try {
            const managerId = selectedProject?.pro_manager;
            if (!managerId) {
                setOrganizerOptions([]);
                return;
            }
            const request = await UserService.getAllUsers();
            const users = request?.data || [];
            const manager = users.find((u) => String(u.user_id).trim() === String(managerId).trim());
            if (manager) {
                setOrganizerOptions([{ value: manager.name, label: `${manager.name} (${manager.user_id})` }]);
                form.setFieldsValue({ organizer: manager.name });
            } else {
                setOrganizerOptions([]);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const resolveEmailsByName = (names) => {
        if (!Array.isArray(names) || names.length === 0) return [];
        return names
            .map((name) => allUsers.find((u) => u.name === name)?.email)
            .filter(Boolean);
    };

    const sendMeetingNotifications = async (values, meetingLabel) => {
        try {
            const names = [
                ...(values.organizer ? [values.organizer] : []),
                ...(Array.isArray(values.attendees) ? values.attendees : []),
            ];
            const optionalEmails = Array.isArray(values.optional_attendee_emails)
                ? values.optional_attendee_emails.filter(Boolean)
                : [];
            const toEmails = [...new Set([...resolveEmailsByName(names), ...optionalEmails])];

            if (toEmails.length === 0) return;


            await EmailConfigService.sendReminder({
                to: toEmails,
                subject: `Meeting Invite: ${values.meeting_title || meetingLabel}`,
                html: `
                    <p>Hi,</p>
                    <p>You have been added to a meeting.</p>
                 <p><strong>Project:</strong> ${projectName || "-"}</p>
                    <p><strong>Title:</strong> ${values.meeting_title || "-"}</p>
                    <p><strong>Date:</strong> ${values.meeting_date ? dayjs(values.meeting_date).format("DD-MMM-YYYY") : "-"}</p>
                    <p><strong>Time:</strong> ${values.start_time ? dayjs(values.start_time).format("hh:mm A") : "-"} - ${values.end_time ? dayjs(values.end_time).format("hh:mm A") : "-"}</p>
           <p><strong>Agenda:</strong> ${values.purpose_agenda || "-"}</p>
                    ${values.location ? `<p><strong>Location:</strong> ${values.location}</p>` : ""}
                    ${values.meeting_link ? `<p><strong>Meeting Link:</strong> ${values.meeting_link}</p>` : ""}
            
                `,
            });
        } catch (e) {
            console.error("Meeting email notification failed:", e);
        }
    };

    const getProjectOptions = async () => {
        try {
            const request = await ProjectService.getAllProject();
            if (request?.status) {
                let rows = request.data || [];

                if (!perm.view_all) {
                    const uid = String(currentUser?.uid ?? "").trim();
                    rows = rows.filter((p) => {
                        let engineers = p.pro_engineer;
                        if (typeof engineers === "string") {
                            try { engineers = JSON.parse(engineers); } catch { engineers = []; }
                        }
                        const isEngineer = (engineers || []).some((id) => String(id).trim() === uid);
                        return (
                            String(p.pro_manager ?? "").trim() === uid ||
                            String(p.added_by ?? "").trim() === uid ||
                            isEngineer
                        );
                    });
                }

                setProjectOptions(
                    rows.map((p) => ({
                        value: p.id,
                        label: p.pro_name,
                        pro_no: p.pro_no,
                        pro_engineer: p.pro_engineer,
                        pro_manager: p.pro_manager,
                    }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };



    const ensureProjectInOptions = async (pro_id) => {
        setProjectOptions((prev) => {
            if (prev.some((p) => p.value === pro_id)) return prev;
            return prev;
        });
        try {
            const already = projectOptions.some((p) => p.value === pro_id);
            if (already) return;
            const request = await ProjectService.getAllProjectById(pro_id);
            if (request?.status && request?.data) {
                const p = request.data;
                setProjectOptions((prev) =>
                    prev.some((opt) => opt.value === p.id)
                        ? prev
                        : [...prev, {
                            value: p.id,
                            label: p.pro_name,
                            pro_no: p.pro_no,
                            pro_engineer: p.pro_engineer,
                            pro_manager: p.pro_manager,
                        }]
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getLastMeeting = async () => {

        try {
            const request = await MeetingService.getLastMeeting();
            let nextNo = "001";
            if (request?.status && request?.data?.meeting_code) {
                const seqPart = request.data.meeting_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setMeetingCode(`MTG-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getMeetingById = async (id) => {
        try {
            setLoadingRecord(true);
            const request = await MeetingService.getMeetingById(id);
            if (request?.status && request?.data) {
                const data = request.data;
                setMeetingCode(data.meeting_code || "MTG-001");
                if (data.pro_id) {
                    setSelectedProjectId(data.pro_id);
                    ensureProjectInOptions(data.pro_id);
                }

                let attachments = data.attachments;
                if (typeof attachments === "string") {
                    try { attachments = JSON.parse(attachments || "[]"); } catch { attachments = []; }
                }
                setFileList(
                    (attachments || []).map((f, idx) => ({
                        uid: `existing-${idx}`,
                        name: f.filename || f.name,
                        status: "done",
                        url: f.path?.startsWith("http") ? f.path : `${ENDPOINT.base}/${f.path}`,
                        response: { file: f },
                    }))
                );

                let attendees = data.attendees;
                if (typeof attendees === "string") {
                    try { attendees = JSON.parse(attendees || "[]"); } catch { attendees = []; }
                }
                let optionalAttendees = data.optional_attendees;
                if (typeof optionalAttendees === "string") {
                    try { optionalAttendees = JSON.parse(optionalAttendees || "[]"); } catch { optionalAttendees = []; }
                }
                let optionalAttendeeEmails = data.optional_attendee_emails;
                if (typeof optionalAttendeeEmails === "string") {
                    try { optionalAttendeeEmails = JSON.parse(optionalAttendeeEmails || "[]"); } catch { optionalAttendeeEmails = []; }
                }

                form.setFieldsValue({
                    meeting_title: data.meeting_title,
                    meeting_type: data.meeting_type,
                    purpose_agenda: data.purpose_agenda,
                    location: data.location,
                    meeting_link: data.meeting_link,
                    meeting_date: data.meeting_date ? dayjs(data.meeting_date) : null,
                    start_time: data.start_time ? dayjs(data.start_time, "hh:mm A") : null,
                    end_time: data.end_time ? dayjs(data.end_time, "hh:mm A") : null,
                    timezone: data.timezone,
                    is_recurring: data.is_recurring,
                    organizer: data.organizer,
                    attendees: attendees || [],
                    optional_attendees: optionalAttendees || [],
                    optional_attendee_emails: optionalAttendeeEmails || [],
                    notify_attendees: data.notify_attendees,
                    related_requirement: data.related_requirement,
                    related_milestone: data.related_milestone,
                    related_task: data.related_task,
                    priority: data.priority,
                    status: data.status,
                    remarks: data.remarks,
                });
            } else {
                messageApi.error(request?.message || "Meeting not found");
            }
        } catch (error) {
            console.error("Error fetching meeting:", error);
            messageApi.error("Failed to load meeting");
        } finally {
            setLoadingRecord(false);
        }
    };

    const uploadAttachments = async () => {
        const uploadedFiles = [];
        for (const f of fileList) {
            if (!f.originFileObj) continue;
            try {
                const formData = new FormData();
                formData.append("file", f.originFileObj);
                const res = await fetch(`${ENDPOINT.Meeting}/upload-file`, {
                    method: "POST",
                    body: formData,
                });
                const result = await res.json();
                if (result.status && result.success) {
                    uploadedFiles.push(result.file);
                }
            } catch (e) {
                console.error("Attachment upload failed:", e);
            }
        }
        return uploadedFiles;
    };

    const getDuration = (start, end) => {
        if (!start || !end) return "-";
        let e = end;
        if (e.isBefore(start)) e = e.add(1, "day");
        const minutes = e.diff(start, "minute");
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hrs && mins) return `${hrs}h ${mins}m`;
        if (hrs) return `${hrs} Hour${hrs > 1 ? "s" : ""}`;
        return `${mins}m`;
    };

    const buildPayload = async (values, isDraft) => {
     
        const user = await UserService.getOmeUser();
        const newlyUploaded = await uploadAttachments();
        const existingAttachments = fileList
            .filter((f) => !f.originFileObj)
            .map((f) => f.response?.file)
            .filter(Boolean);
        const attachments = [...existingAttachments, ...newlyUploaded];

        return {
            meeting_title: values.meeting_title,
            meeting_type: Array.isArray(values.meeting_type) ? values.meeting_type[0] : values.meeting_type,
            purpose_agenda: values.purpose_agenda,
            location: values.location,
            meeting_link: values.meeting_link,
            meeting_date: values.meeting_date ? dayjs(values.meeting_date).format("YYYY-MM-DD") : null,
            start_time: values.start_time ? dayjs(values.start_time).format("hh:mm A") : null,
            end_time: values.end_time ? dayjs(values.end_time).format("hh:mm A") : null,
            duration: getDuration(values.start_time, values.end_time),
            timezone: values.timezone,
            is_recurring: !!values.is_recurring,
            organizer: values.organizer,
            attendees: JSON.stringify(values.attendees || []),
            optional_attendees: JSON.stringify(values.optional_attendees || []),
            optional_attendee_emails: JSON.stringify(values.optional_attendee_emails || []),
            notify_attendees: values.notify_attendees !== false,
            related_requirement: values.related_requirement,
            related_milestone: values.related_milestone,
            related_task: values.related_task,
            priority: values.priority,
            status: isDraft ? "Scheduled" : (values.status || "Scheduled"),
            attachments: JSON.stringify(attachments),
            remarks: values.remarks,
            meeting_code: meetingCode,
            pro_id: projectId,
            pro_no: projectNo,
            is_draft: isDraft,
            added_by: user?.uid,
        };
    };

    const submitMeeting = async (values, isDraft = false) => {
        try {
            isDraft ? setSavingDraft(true) : setSubmitting(true);
            const payload = await buildPayload(values, isDraft);

            const request = isEditMode
                ? await MeetingService.updateMeeting(payload, meetingDbId)
                : await MeetingService.createMeeting(payload);

            if (request?.status) {
                messageApi.success(
                    request.message || (isDraft ? "Saved as draft" : isEditMode ? "Meeting updated" : "Meeting created")
                );

                if (!isDraft && values.notify_attendees !== false) {
                    await sendMeetingNotifications(values, meetingCode);
                }

                navigate(-1);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error submitting meeting:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSavingDraft(false);
            setSubmitting(false);
        }
    };

    const onFinish = (values) => {
        submitMeeting(values, false);
    };

    const handleSaveDraft = () => {
        const values = form.getFieldsValue();
        submitMeeting(values, true);
    };

    const headerLabel = isEditMode ? (readOnly ? "View Meeting" : "Edit Meeting") : "Add Meeting";

    return (
        <>
            <style>{compactFormStyle}</style>
            {contextHolder}

            <Card
                loading={loadingRecord}
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(-1)}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {headerLabel}
                                    <Tag color="blue" style={{ fontSize: "16px", padding: "6px 16px", borderRadius: "8px", fontWeight: 600 }}>
                                        {meetingCode}
                                    </Tag>
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    <Space>
                        {!readOnly && (
                            <>
                                <Button size="large" loading={savingDraft} onClick={handleSaveDraft}>
                                    Save as Draft
                                </Button>
                                <Button
                                    type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                                    style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                >
                                    {isEditMode ? "Update Meeting" : "Save Meeting"}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            disabled={readOnly}
                            initialValues={{ priority: "High", status: "Scheduled", notify_attendees: true, timezone: "(GMT+05:30) Asia/Kolkata" }}
                        >
                            <Card title={<CardTitle no={1} text="Meeting Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Project" required>
                                            <Select
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select project"
                                                options={projectOptions}
                                                value={selectedProjectId}
                                                onChange={(v) => setSelectedProjectId(v)}
                                                filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="meeting_title" label="Meeting Title" rules={[{ required: true, message: "Please enter meeting title" }]}>
                                            <Input size="large" placeholder="Enter meeting title" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="meeting_type" label="Meeting Type" rules={[{ required: true, message: "Please select meeting type" }]}>
                                            <Select
                                                options={meetingTypeOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select meeting type"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Meeting Code">
                                            <Input size="large" readOnly value={meetingCode} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="purpose_agenda" label="Purpose / Agenda" rules={[{ required: true, message: "Please enter purpose or agenda" }]}>
                                            <TextArea rows={5} size="large" maxLength={500} showCount placeholder="Enter purpose or agenda of the meeting..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="location" label="Location">
                                            <Input size="large" placeholder="Enter meeting location / room name" />
                                        </Form.Item>
                                        <Form.Item name="meeting_link" label="Meeting Link (if online)">
                                            <Input size="large" placeholder="https://meet.google.com/..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={2} text="Date & Time" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="meeting_date" label="Date" rules={[{ required: true, message: "Please select date" }]}>
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MMM-YYYY" placeholder="Select date" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="start_time" label="Start Time" rules={[{ required: true, message: "Please select start time" }]}>
                                            <TimePicker size="large" style={{ width: "100%" }} format="hh:mm A" placeholder="Select start time" use12Hours />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="end_time" label="End Time" rules={[{ required: true, message: "Please select end time" }]}>
                                            <TimePicker size="large" style={{ width: "100%" }} format="hh:mm A" placeholder="Select end time" use12Hours />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item label="Duration">
                                            <Input size="large" readOnly value={getDuration(summary.start_time, summary.end_time)} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Form.Item name="timezone" label="Time Zone">
                                            <Select options={timeZoneOptions} size="large" placeholder="Select time zone" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} style={{ display: "flex", alignItems: "center", gap: 0 }}>
                                        <Form.Item name="is_recurring" label="Recurring Meeting" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Enable if this meeting repeats on a regular basis
                                        </Text>
                                    </Col>
                                </Row>
                            </Card> 
                            <Card title={<CardTitle no={3} text="Participants" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="organizer" label="Organizer" rules={[{ required: true, message: "Please select organizer" }]}>
                                            <Select options={organizerOptions} size="large" showSearch allowClear placeholder="Select organizer" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="attendees" label="Attendees" rules={[{ required: true, message: "Please select attendees" }]}>
                                            <Select options={assigneeOptions} mode="multiple" size="large" showSearch allowClear placeholder="Select attendees" maxTagCount={2} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="optional_attendees" label="Optional Attendees">
                                            <Select
                                                options={optionalAttendeeOptions}
                                                mode="tags"
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Type to add optional attendee"
                                                maxTagCount={2}
                                                searchValue={optionalAttendeeSearch}
                                                onSearch={(v) => setOptionalAttendeeSearch(v)}
                                                onBlur={() => {
                                                    const trimmed = optionalAttendeeSearch.trim();
                                                    if (trimmed) {
                                                        const current = form.getFieldValue("optional_attendees") || [];
                                                        if (!current.includes(trimmed)) {
                                                            form.setFieldsValue({ optional_attendees: [...current, trimmed] });
                                                        }
                                                        setOptionalAttendeeSearch("");
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            name="optional_attendee_emails"
                                            label="Optional Attendee Emails"
                                            rules={[{
                                                validator: (_, value) => {
                                                    const emails = value || [];
                                                    const invalid = emails.find((e) => !/^\S+@\S+\.\S+$/.test(e));
                                                    return invalid ? Promise.reject("Enter valid email(s)") : Promise.resolve();
                                                },
                                            }]}
                                        >
                                            <Select
                                                mode="tags"
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Type optional attendee email(s) and press enter"
                                                maxTagCount={2}
                                                open={false}
                                                searchValue={optionalAttendeeEmailSearch}
                                                onSearch={(v) => setOptionalAttendeeEmailSearch(v)}
                                                onBlur={() => {
                                                    const trimmed = optionalAttendeeEmailSearch.trim();
                                                    if (trimmed) {
                                                        const current = form.getFieldValue("optional_attendee_emails") || [];
                                                        if (!current.includes(trimmed)) {
                                                            form.setFieldsValue({ optional_attendee_emails: [...current, trimmed] });
                                                        }
                                                        setOptionalAttendeeEmailSearch("");
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="notify_attendees" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Checkbox>Notify all attendees via email</Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={4} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="related_requirement" label="Related Requirement">
                                            <Select options={requirementOptions} size="large" showSearch allowClear placeholder="Select requirement" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="related_milestone" label="Related Milestone">
                                            <Select options={milestoneOptions} size="large" showSearch allowClear placeholder="Select milestone" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="related_task" label="Related Task (if any)">
                                            <Select options={taskOptions} size="large" showSearch allowClear placeholder="Select task" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Please select priority" }]}>
                                            <Select options={priorityOptions} size="large" placeholder="Select priority" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Form.Item name="status" label="Status" rules={[{ required: true, message: "Please select status" }]}>
                                            <Select options={statusOptions} size="large" placeholder="Select status" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                fileList={fileList}
                                                multiple
                                                disabled={readOnly}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                beforeUpload={(file) => {
                                                    setFileList((prev) => [...prev, { uid: `${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file }]);
                                                    return false;
                                                }}
                                                onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
                                                style={{ padding: "6px 0" }}
                                            >
                                                <p className="ant-upload-drag-icon" style={{ margin: "4px 0" }}><InboxOutlined /></p>
                                                <p className="ant-upload-text" style={{ fontSize: 12, margin: 0 }}>Click to upload or drag and drop</p>
                                                <p className="ant-upload-hint" style={{ fontSize: 11 }}> </p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={5} size="large" maxLength={300} showCount placeholder="Enter remarks (optional)..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Meeting Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Project", projectName],
                                    ["Meeting Title", summary.meeting_title || "-"],
                                    ["Meeting Type", Array.isArray(summary.meeting_type) ? summary.meeting_type[0] : summary.meeting_type || "-"],
                                    ["Date & Time", summary.meeting_date ? dayjs(summary.meeting_date).format("DD-MMM-YYYY") : "-"],
                                    ["Duration", getDuration(summary.start_time, summary.end_time)],
                                    ["Organizer", summary.organizer || "-"],
                                    ["Attendees", Array.isArray(summary.attendees) ? summary.attendees.length : "-"],
                                    ["Location", summary.location || "-"],
                                    ["Status", summary.status || "Scheduled"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Add a clear meeting title and agenda.</li>
                                <li>Invite only relevant stakeholders.</li>
                                <li>Attach related documents before the meeting.</li>
                                <li>Share meeting link for online meetings.</li>
                                <li>Take meeting minutes and action items.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {["View Project", "Meeting List", "Meeting Minutes", "Calendar View", "Meeting Reports"].map((label) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(-1)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>

                {!readOnly && (
                    <div style={{ textAlign: "right", marginTop: 16 }}>
                        <Button size="large" style={{ marginRight: 8 }} onClick={() => form.resetFields()}>
                            Reset
                        </Button>
                        <Button size="large" loading={savingDraft} onClick={handleSaveDraft} style={{ marginRight: 8 }}>
                            Save as Draft
                        </Button>
                        <Button
                            type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            {isEditMode ? "Update Meeting" : "Save Meeting"}
                        </Button>
                    </div>
                )}
            </Card >
        </>
    );
};

export default AddMeeting;